'use client'

import { useCallback, useEffect, useState, type MouseEvent, type ReactElement } from "react"
import { Link, type To } from "react-router-dom"
import {
  ArrowRight,
  ChevronRight,
  Menu,
  QrCode,
  CheckCircle,
  X,
} from "lucide-react"
import { AnimatedGroup } from "@/components/ui/animated-group"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { ShapeLandingBackground } from "@/components/ui/shape-landing-hero"
import { ShaderAnimation } from "@/components/ui/shader-animation"

const transitionVariants = {
  item: {
    hidden: {
      opacity: 0,
      filter: "blur(12px)",
      y: 12,
    },
    visible: {
      opacity: 1,
      filter: "blur(0px)",
      y: 0,
      transition: {
        type: "spring",
        bounce: 0.3,
        duration: 1.5,
      },
    },
  },
}

type MenuItem = {
  name: string
  to: To
}

const menuItems: MenuItem[] = [
  { name: "AI Features", to: "/ai-features" },
  { name: "Pricing", to: "/pricing" },
  { name: "Calculator", to: "/cost-calculator" },
  { name: "Stories", to: "/stories" },
  { name: "Blog", to: "/blog" },
  { name: "Talk to Braise", to: "/talk-to-braise" },
  { name: "About", to: "/about" },
]

type HeroSectionProps = {
  onStartDemo?: () => void
}

export function HeroSection({ onStartDemo }: HeroSectionProps = {}): ReactElement {
  const handleDemoClick = useCallback(() => {
    if (onStartDemo) {
      onStartDemo()
      return
    }

    scrollToSection("demo")
  }, [onStartDemo])

  return (
    <>
      <HeroHeader />
      <ShapeLandingBackground
        as="main"
        className="bg-white text-slate-900"
        minHeightClassName="min-h-0"
        showDecorations={false}
        backgroundSlot={
          <>
            <div className="pointer-events-none h-full w-full">
              <ShaderAnimation intensity={1.4} lineWidth={0.0035} />
            </div>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/55 via-white/92 to-white" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_90%_10%,_rgba(255,255,255,0.7),_transparent_45%),_radial-gradient(circle_at_80%_0%,_rgba(255,255,255,0.55),_transparent_55%)]" />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.85),_transparent_70%)]" />
          </>
        }
      >
        <section className="relative isolate pt-14 pb-8 sm:pt-18 sm:pb-12 lg:pt-20 lg:pb-14" id="top">
          <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-8 px-4 text-center sm:px-6">
            <AnimatedGroup
              variants={{
                container: {
                  visible: {
                    transition: {
                      staggerChildren: 0.08,
                      delayChildren: 0.2,
                    },
                  },
                },
                ...transitionVariants,
              }}
              className="space-y-6 text-center"
            >
              <span className="inline-flex flex-wrap items-center justify-center gap-2 rounded-full border border-blue-500/40 bg-blue-50/90 px-5 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-blue-700 shadow-[0_8px_26px_rgba(59,130,246,0.28)] ring-1 ring-blue-100/80">
                QR First POS
              </span>
              <h1 className="flex flex-col items-center text-center font-medium leading-tight text-balance text-slate-900">
                <span className="w-full text-balance text-[clamp(1.45rem,7vw,3.6rem)] font-semibold leading-[1.05] tracking-[-0.04em] text-slate-900">
                  10× Revenue with Personalized Dining
                </span>
                <span className="mt-3 flex flex-wrap items-center justify-center gap-2 text-balance text-[clamp(1rem,4vw,2rem)] tracking-[-0.01em] text-slate-700">
                  <span className="text-slate-500">Powered by</span>
                  <span className="bg-gradient-to-r from-emerald-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
                    Next‑Gen AI Agents
                  </span>
                </span>
              </h1>
              <p className="mx-auto max-w-3xl text-balance text-base leading-relaxed text-slate-700 sm:text-lg">
                Braise is the Apple-quality hospitality OS for cafés, food trucks, and tiny restaurant groups. Guests order anywhere,
                AI routes every ticket, and rush-mode UI keeps the line calm without an IT crew.
              </p>
              <p className="text-balance text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 sm:text-sm sm:tracking-[0.35em]">
                POS FOR SMALL RESTAURANTS · CAFÉ POINT-OF-SALE · FOOD TRUCK POS · HOSPITALITY AI AUTOMATION
              </p>
              <div className="flex w-full flex-col items-center justify-center gap-3 pt-2 sm:flex-row">
                <Button
                  size="lg"
                  className="h-12 w-full max-w-xs rounded-full bg-blue-600 px-8 text-base font-semibold tracking-tight text-white shadow-lg shadow-blue-500/30 transition hover:-translate-y-0.5 hover:bg-blue-500 sm:w-auto"
                  onClick={handleDemoClick}
                >
                  Start Your Free Personalized Demo
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="h-12 w-full max-w-xs rounded-full border-2 border-blue-100 bg-white/80 px-8 text-base font-semibold text-blue-700 shadow-sm shadow-blue-100/80 transition hover:bg-blue-50 hover:text-blue-800 sm:w-auto"
                >
                  <Link
                    to="/ai-features"
                    className="inline-flex items-center justify-center gap-2"
                  >
                    Explore 10+ AI Features
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </Link>
                </Button>
              </div>
              <div className="flex w-full flex-wrap items-center justify-center gap-x-2 gap-y-3 px-1 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-emerald-700 sm:flex-nowrap sm:gap-y-0 sm:text-xs sm:tracking-[0.22em]">
                <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-center shadow-[0_10px_26px_rgba(16,185,129,0.18)] ring-1 ring-emerald-100/80 backdrop-blur-[1px]">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-500" aria-hidden />
                  No IT Crew Required
                </span>
                <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-center shadow-[0_10px_26px_rgba(16,185,129,0.18)] ring-1 ring-emerald-100/80 backdrop-blur-[1px]">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-500" aria-hidden />
                  Go Live in 2 Hours
                </span>
                <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-center shadow-[0_10px_26px_rgba(16,185,129,0.18)] ring-1 ring-emerald-100/80 backdrop-blur-[1px]">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-500" aria-hidden />
                  Pay $0 for 6 Months
                </span>
              </div>
            </AnimatedGroup>
          </div>
        </section>

      </ShapeLandingBackground>
    </>
  )
}

export const HeroHeader = (): ReactElement => {
  const [menuState, setMenuState] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  const handleMenuClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, destination: To) => {
      if (typeof destination === "object" && destination !== null && "hash" in destination && destination.hash) {
        event.preventDefault()
        scrollToSection(String(destination.hash))
      }
      setMenuState(false)
    },
    [setMenuState],
  )

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  return (
    <header>
      <nav data-state={menuState && "active"} className="fixed inset-x-0 top-0 z-40 px-2">
        <div
          className={cn(
            "relative flex w-full items-center justify-between border px-4 py-4 text-slate-900 transition-all duration-300 lg:px-8",
            isScrolled
              ? "mx-auto mt-2 max-w-6xl rounded-2xl border-slate-200 bg-white/95 shadow-[0_25px_60px_rgba(15,23,42,0.12)] backdrop-blur-xl"
              : "mt-0 max-w-full rounded-none border-transparent bg-transparent shadow-none",
          )}
        >
          <Link
            to="/"
            aria-label="Braise home"
            className={cn("flex items-center gap-3 text-slate-900")}
          >
            <Logo isLight />
          </Link>

          <div className={cn("hidden items-center gap-4 lg:flex")}>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="group relative h-11 rounded-full border border-blue-500/45 bg-gradient-to-br from-blue-50/95 via-white to-blue-100/80 px-5 text-sm font-semibold text-blue-700 shadow-[0_12px_32px_rgba(59,130,246,0.35)] ring-1 ring-blue-100/80 backdrop-blur-md transition hover:-translate-y-0.5 hover:bg-blue-50/95 hover:text-blue-800 hover:shadow-[0_18px_55px_rgba(59,130,246,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60"
            >
              <Link to="/auth/login">Log in</Link>
            </Button>
            <Button
              asChild
              size="lg"
              className="h-11 rounded-full bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-500 px-6 text-sm font-semibold text-white shadow-[0_20px_45px_rgba(59,130,246,0.35)] transition hover:-translate-y-0.5 hover:brightness-110"
            >
              <Link to="/auth/signup">Get started</Link>
            </Button>
          </div>

          <button
            type="button"
            onClick={() => setMenuState((previous) => !previous)}
            aria-label={menuState ? "Close menu" : "Open menu"}
            className="ml-auto flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white/70 text-slate-900 transition hover:bg-white lg:hidden"
          >
            <Menu className={cn("h-5 w-5 transition-all", menuState && "scale-0 opacity-0")} />
            <X className={cn("absolute h-5 w-5 transition-all", menuState ? "scale-100 opacity-100" : "scale-0 opacity-0")} />
          </button>

          <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-full items-center justify-center px-2 lg:flex">
            <ul
              className={cn(
                "pointer-events-auto flex items-center gap-8 transition-all",
                isScrolled ? "text-sm font-medium text-slate-800" : "text-base font-semibold text-slate-900"
              )}
            >
              {menuItems.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.to}
                    className="transition hover:text-slate-900"
                    onClick={(event) => handleMenuClick(event, item.to)}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

          <div
            className={cn(
              "mt-3 grid gap-6 rounded-2xl border border-slate-200 bg-white/95 p-6 text-sm text-slate-900 shadow-[0_30px_80px_rgba(15,23,42,0.12)] backdrop-blur-2xl transition-all lg:hidden",
              menuState ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
            )}
          >
            <ul className="grid gap-4 text-slate-600">
              {menuItems.map((item) => (
                <li key={item.name}>
                <Link
                  to={item.to}
                  className="flex items-center justify-between rounded-2xl border border-slate-200 px-4 py-3 text-base transition hover:border-blue-200 hover:bg-blue-50/60 hover:text-slate-900"
                  onClick={(event) => handleMenuClick(event, item.to)}
                >
                  {item.name}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-3">
            <Button
              asChild
              variant="outline"
              size="lg"
              className="group relative h-11 rounded-2xl border border-blue-500/45 bg-gradient-to-br from-blue-50/95 via-white to-blue-100/80 px-4 text-sm font-semibold text-blue-700 shadow-[0_12px_32px_rgba(59,130,246,0.35)] ring-1 ring-blue-100/80 backdrop-blur-md transition hover:-translate-y-0.5 hover:bg-blue-50/95 hover:text-blue-800 hover:shadow-[0_18px_55px_rgba(59,130,246,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60"
            >
              <Link to="/auth/login">Log in</Link>
            </Button>
            <Button
              asChild
              size="lg"
              className="h-12 rounded-2xl bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-500 px-4 text-base font-semibold text-white shadow-[0_20px_45px_rgba(59,130,246,0.35)] transition hover:-translate-y-0.5 hover:brightness-110"
            >
              <Link to="/auth/signup" className="inline-flex items-center justify-center gap-2">
                Get started
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </Button>
          </div>
        </div>
      </nav>
    </header>
  )
}

const Logo = ({ isLight = false }: { isLight?: boolean }): ReactElement => (
  <div className="flex items-center gap-3">
    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-slate-900 shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
      <QrCode className="h-5 w-5" aria-hidden />
    </span>
    <div className={cn("flex flex-col leading-tight", isLight ? "text-slate-900" : "text-white")}>
      <span className="text-base font-semibold tracking-tight">Braise</span>
      <span className={cn("text-xs uppercase tracking-[0.32em]", isLight ? "text-slate-500" : "text-white/60")}>POS OS</span>
    </div>
  </div>
)

const scrollToSection = (hash: string): void => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return
  }

  const normalizedHash = hash.startsWith("#") ? hash.slice(1) : hash
  if (!normalizedHash) {
    return
  }

  const target = document.getElementById(normalizedHash)

  if (target) {
    target.scrollIntoView({ behavior: "smooth", block: "start" })
    const url = new URL(window.location.href)
    url.hash = `#${normalizedHash}`
    window.history.replaceState(null, "", url)
    return
  }

  window.location.assign(`/#${normalizedHash}`)
}
